import {useEffect, useMemo, useReducer} from 'react';
import {
  DateRange,
  RANGES,
  getThisSchoolYear
} from '@albert-io/atomic/organisms/DatePicker/DatePicker.helpers';
import moment from 'moment-timezone';
import {removeQueryParams} from 'client/history';

import {
  DIMENSIONS,
  METRIC_COLUMNS,
  columnIsAvailable,
  getArchivedDateRange,
  getClassroomQuery,
  isDimension
} from 'client/Reports/reports.utils';
import {CLASSROOM_STATUSES} from 'client/constants';

import {genericCancelableMandarkRequest, genericMandarkRequest} from 'resources/mandark.resource';
import {groupBy, isEqual, uniqBy} from 'lodash';

import {
  AnalyticsApiResponse,
  Dimension,
  MetricColumn,
  Pagination,
  QueryVariables,
  Sort,
  TopLevelFilter
} from './reports.types';

const SECONDARY_PAGINATION_SIZE = 10;

export interface LocationProps {
  query: {
    assignmentId?: string; // @todo uuid type? also rm ID
    attempt?: string;
    end?: string;
    guideLevel?: string; // @todo uuid type?
    limit?: string;
    performanceCalculateBy?: 'accuracy' | 'grade';
    metrics?: string;
    page?: string;
    range?: DateRange;
    s?: string;
    sort?: string;
    start?: string;
    studentId?: string; // @todo uuid type?
    subject?: string; // @todo uuid type?
    standardSets?: string;
    personalization?: string;
    guide?: string;
  };
  pathname: string;
}

export interface ParamProps {
  topLevelFilterType?: TopLevelFilter;
  topLevelFilterId?: string;
  assignmentId?: string;
  questionId?: string;
  standardId?: string;
  studentId?: string;
}

type ActionTypes = typeof actionTypes;

export interface State {
  attemptNumber: number | 'mostRecent';
  assignmentId: string;
  topLevelFilterType: TopLevelFilter;
  topLevelFilterId: string;
  data: AnalyticsApiResponse[];
  dimensions: Dimension[];
  error: boolean;
  keyword?: string;
  loading: boolean;
  loadingMore: boolean;
  questionId: string;
  start: moment.Moment;
  end: moment.Moment;
  range: DateRange;
  guideLevel: string[];
  limit: string;
  totals: {type: Dimension; value: number}[];
  performanceCalculateBy: 'accuracy' | 'grade';
  metrics: MetricColumn[];
  page: string;
  path: string[];
  sort: Sort[];
  standardId: string;
  standardSets: string;
  autoPersonalization: boolean;
  studentId: string;
  subject: string;
  variables: QueryVariables;
  cancelRequest: () => void;
}

interface Action {
  type: ValueOf<ActionTypes>;
  payload: any;
}

export type DispatchActions = Record<keyof ActionTypes, (payload: any) => void>;

interface Route {
  location: LocationProps;
  params: ParamProps;
}

export type ContextType = State & DispatchActions & {query: LocationProps['query']};

const getMetricColumns = (columnKeys: string[]) => {
  const keySet = new Set(columnKeys);
  return METRIC_COLUMNS.filter((c) => keySet.has(c.key));
};

const buildMetricsRequest = (
  columns: MetricColumn[],
  dimensions: Dimension[],
  variables: QueryVariables,
  performanceCalculateBy: 'accuracy' | 'grade',
  topLevelFilterType: TopLevelFilter
) => {
  const metricsToLoad = Array.from(
    new Set(
      columns
        .filter((m) => columnIsAvailable(m, dimensions, variables, topLevelFilterType))
        .map((m) => m.getMetricsToLoad(dimensions, variables, {performanceCalculateBy}))
        .flat()
    )
  );

  const partitionedByType = groupBy(metricsToLoad, (m) => m.split('.')[0]);
  return Object.keys(partitionedByType).map((metricType) => {
    return {
      type: metricType,
      fields: partitionedByType[metricType].map((metric) => metric.split('.')[1])
    };
  });
};

export const fetchDataForCsv = async ({
  metrics,
  variables,
  dimensions,
  pagination,
  sort,
  performanceCalculateBy,
  topLevelFilterType
}: Pick<
  State,
  'variables' | 'dimensions' | 'sort' | 'metrics' | 'performanceCalculateBy' | 'topLevelFilterType'
> & {
  pagination: Pagination;
}): Promise<{
  data: AnalyticsApiResponse[];
  totals: {type: Dimension; value: number}[];
}> => {
  const filters = Object.values(variables);
  const filteredFilters = filters.filter((item) => !!item);

  const metricsRequest = buildMetricsRequest(
    metrics,
    dimensions,
    variables,
    performanceCalculateBy,
    topLevelFilterType
  );

  const query = {
    dimensions,
    metrics: metricsRequest,
    filters: filteredFilters,
    pagination,
    sorts: sort
  };

  const response = await genericMandarkRequest(
    'post',
    {
      resourcePath: ['analytics', 'v1', 'load']
    },
    {
      query
    }
  );
  return response.toJS();
};

const decoratedFetchData = async (
  {
    data: prevData,
    fetchMore,
    metrics,
    variables,
    dimensions,
    limit,
    page,
    sort,
    performanceCalculateBy,
    cancelRequest,
    topLevelFilterType
  }: Pick<
    State,
    | 'data'
    | 'metrics'
    | 'variables'
    | 'dimensions'
    | 'limit'
    | 'page'
    | 'sort'
    | 'performanceCalculateBy'
    | 'cancelRequest'
    | 'topLevelFilterType'
  > & {fetchMore: boolean},
  setLoading: (payload: boolean) => void,
  setData: (payload: any) => void,
  setTotals: (payload: {type: Dimension; value: number}[]) => void,
  setError: (payload: boolean) => void,
  setCancelRequest: (payload: () => void) => void,
  setLoadingMore: (payload: boolean) => void
) => {
  cancelRequest();
  setError(false);

  const primaryPagination = {
    type: dimensions[0],
    value: {
      limit: Number(limit),
      offset: Number(limit) * (Number(page) - 1)
    }
  };
  const pagination =
    dimensions.length === 1
      ? [primaryPagination]
      : [
          primaryPagination,
          {
            type: dimensions[1],
            value: {
              limit: SECONDARY_PAGINATION_SIZE,
              offset: 0
            }
          }
        ];

  if (fetchMore && dimensions.length > 1) {
    setLoadingMore(true);
    const numSecondaryDimensionItems = uniqBy(
      prevData.map((el) => el.dimensions[1].value),
      'id'
    ).length;
    pagination[1].value.offset = numSecondaryDimensionItems;
  } else {
    setTotals([]);
    setLoading(true);
    setData([]);
  }

  const metricsRequest = buildMetricsRequest(
    metrics,
    dimensions,
    variables,
    performanceCalculateBy,
    topLevelFilterType
  );

  try {
    const filters = Object.values(variables);
    const filteredFilters = filters.filter((item) => !!item);

    const {promise, cancel} = await genericCancelableMandarkRequest(
      'post',
      {
        resourcePath: ['analytics', 'v1', 'load']
      },
      {
        query: {
          dimensions,
          metrics: metricsRequest,
          filters: filteredFilters,
          pagination,
          sorts: sort
        }
      }
    );

    setCancelRequest(cancel);

    const response = await promise;
    const data = response.get('data').toJS();
    const totals = response.get('totals').toJS();
    setTotals(totals);
    if (fetchMore) {
      setData([...prevData, ...data]);
    } else {
      setData(data);
    }
  } catch (e) {
    setError(true);
  }
  setLoading(false);
  setLoadingMore(false);
};

function getPathFromPathname(pathname: string = ''): (string | Dimension)[] {
  return pathname.split('/');
}

const actionTypes = {
  setAssignmentId: 'SET_ASSIGNMENT_ID',
  setAttemptNumber: 'SET_ATTEMPT_NUMBER',
  setTopLevelFilters: 'SET_TOP_LEVEL_FILTERS',
  setData: 'SET_DATA',
  setTotals: 'SET_TOTALS',
  fetchData: 'FETCH_DATA',
  fetchMoreData: 'FETCH_MORE_DATA',
  setDate: 'SET_DATE',
  setDimensions: 'SET_DIMENSIONS',
  setError: 'SET_ERROR',
  setGuideLevelId: 'SET_GUIDE_LEVEL_ID',
  setKeyword: 'SET_KEYWORD',
  setLimit: 'SET_LIMIT',
  setLoading: 'SET_LOADING',
  setLoadingMore: 'SET_LOADING_MORE',
  setPerformanceCalculateBy: 'SET_MASTERY_CALCULATE_BY',
  setMetric: 'SET_METRIC',
  setPage: 'SET_PAGE',
  setPath: 'SET_PATH',
  setQuestionId: 'SET_QUESTION_ID',
  setSort: 'SET_SORT',
  setStandardId: 'SET_STANDARD_ID',
  setStudentId: 'SET_STUDENT_ID',
  setSubject: 'SET_SUBJECT',
  setCancelRequest: 'SET_CANCEL_REQUEST',
  setStandardSets: 'SET_STANDARD_SETS',
  setAutoPersonalization: 'SET_AUTO_PERSONALIZATION',
  clearFilters: 'CLEAR_FILTERS'
};

function reducer(state: State, {type, payload}: Action): State {
  switch (type) {
    case actionTypes.clearFilters: {
      removeQueryParams(
        's',
        'start',
        'end',
        'range',
        'attempt',
        'subject',
        'guideLevel',
        'guide',
        'standardSets',
        'personalization'
      );
      return {
        ...state
      };
    }

    case actionTypes.setData: {
      return {
        ...state,
        data: payload
      };
    }

    case actionTypes.setError: {
      return {
        ...state,
        error: payload
      };
    }

    case actionTypes.setLoading: {
      return {
        ...state,
        loading: payload
      };
    }

    case actionTypes.setLoadingMore: {
      return {
        ...state,
        loadingMore: payload
      };
    }

    case actionTypes.setCancelRequest: {
      return {
        ...state,
        cancelRequest: payload
      };
    }

    case actionTypes.setKeyword: {
      const {dimensions, keyword, variables} = state;

      if (dimensions.length > 1) {
        return state;
      }

      const [dimension] = dimensions;

      // Keyword has been cleared
      if (!payload && keyword) {
        variables[`${dimension?.slice(0, dimension.length - 1)}Filter`] = undefined;

        removeQueryParams('s');

        return {
          ...state,
          variables,
          keyword: payload
        };
      }

      if (!payload) {
        return state;
      }

      if (dimension === DIMENSIONS.assignments) {
        variables.assignmentFilter = {
          type: 'assignments',
          field: 'name',
          operator: 'contains',
          values: [payload]
        };
      }

      if (dimension === DIMENSIONS.questions) {
        variables.questionFilter = {
          type: 'questions',
          field: 'title',
          operator: 'contains',
          values: [payload]
        };
      }

      if (dimension === DIMENSIONS.students) {
        variables.studentFilter = {
          type: 'students',
          field: 'full_name',
          operator: 'search',
          values: [payload]
        };
      }

      if (dimension === DIMENSIONS.standards) {
        variables.standardFilter = {
          type: 'standards',
          field: 'title',
          operator: 'contains',
          values: [payload]
        };
      }
      return {
        ...state,
        variables,
        keyword: payload
      };
    }

    case actionTypes.setAssignmentId: {
      const assignmentId = payload;
      const {variables} = state;

      if (assignmentId) {
        variables.assignmentFilter = {
          type: 'assignments',
          field: 'id',
          operator: 'equals',
          values: [assignmentId]
        };
      } else {
        delete variables.assignmentFilter;
      }

      return {
        ...state,
        assignmentId,
        variables
      };
    }
    case actionTypes.setAttemptNumber: {
      const attemptNumber = payload === 'mostRecent' ? payload : parseInt(payload, 10);
      const {variables} = state;

      delete variables.attemptNumberFilter;
      if (attemptNumber === 'mostRecent') {
        variables.attemptNumberFilter = {
          type: 'guesses',
          operator: 'most_recent'
        };
      } else if (attemptNumber) {
        variables.attemptNumberFilter = {
          type: 'guesses',
          field: 'attempt_number',
          operator: 'equals',
          values: [attemptNumber]
        };
      }

      return {
        ...state,
        attemptNumber,
        variables
      };
    }
    case actionTypes.setTopLevelFilters: {
      const {topLevelFilterType, topLevelFilterId} = payload as {
        topLevelFilterType: TopLevelFilter;
        topLevelFilterId: string;
      };
      const {variables} = state;

      delete variables.classroomFilter;
      delete variables.teacherFilter;
      delete variables.schoolFilter;
      delete variables.districtFilter;

      if (topLevelFilterId) {
        if (topLevelFilterType === 'teachers') {
          variables.teacherFilter = {
            type: 'teachers',
            field: 'id',
            operator: 'equals',
            values: [topLevelFilterId]
          };
        } else if (topLevelFilterType === 'classrooms') {
          variables.classroomFilter = {
            type: 'classrooms',
            field: 'id',
            operator: 'equals',
            values: [topLevelFilterId]
          };
        } else if (topLevelFilterType === 'schools') {
          variables.schoolFilter = {
            type: 'schools',
            field: 'id',
            operator: 'equals',
            values: [topLevelFilterId]
          };
        } else if (topLevelFilterType === 'districts') {
          variables.districtFilter = {
            type: 'districts',
            field: 'id',
            operator: 'equals',
            values: [topLevelFilterId]
          };
        }
      }

      return {
        ...state,
        topLevelFilterType,
        topLevelFilterId,
        variables
      };
    }
    case actionTypes.setDate: {
      const {start, end, range} = payload;
      const sanitizedRange = decodeURIComponent(range) as DateRange;
      const {variables} = state;
      if (start) {
        variables.startDateFilter = {
          type: 'guesses',
          field: 'inserted_at',
          operator: 'gte',
          values: [moment(start).startOf('day').toISOString()]
        };
      } else {
        delete variables.startDateFilter;
      }

      if (end) {
        variables.endDateFilter = {
          type: 'guesses',
          field: 'inserted_at',
          operator: 'lte',
          values: [moment(end).endOf('day').toISOString()]
        };
      } else {
        delete variables.endDateFilter;
      }

      return {
        ...state,
        start,
        end,
        range: sanitizedRange,
        variables
      };
    }
    case actionTypes.setDimensions: {
      const {variables} = state;
      const dimensions = payload;
      removeQueryParams('page', 'sort', 'limit');

      return {
        ...state,
        dimensions,
        page: '1',
        variables: {
          ...variables
        }
      };
    }
    case actionTypes.setGuideLevelId: {
      const guideLevel = payload;
      const {variables} = state;
      if (guideLevel.length) {
        variables.guideLevelFilter = {
          type: 'guide_levels',
          field: 'id',
          operator: 'equals',
          values: guideLevel
        };
        variables.subjectFilter = undefined;
      } else {
        variables.guideLevelFilter = undefined;
      }
      return {
        ...state,
        guideLevel,
        variables
      };
    }

    case actionTypes.setPerformanceCalculateBy: {
      return {
        ...state,
        performanceCalculateBy: payload
      };
    }

    case actionTypes.setMetric: {
      const metrics = payload.split(',');
      const {variables} = state;
      const enabledMetricColumns = getMetricColumns(metrics);

      return {
        ...state,
        metrics: enabledMetricColumns,
        variables
      };
    }
    case actionTypes.setPath: {
      const path = payload;
      return {
        ...state,
        path
      };
    }
    case actionTypes.setAutoPersonalization: {
      return {
        ...state,
        autoPersonalization: payload
      };
    }
    case actionTypes.setQuestionId: {
      const questionId = payload;
      const {variables} = state;
      if (questionId) {
        variables.questionFilter = {
          type: 'questions',
          field: 'id',
          operator: 'equals',
          values: [questionId]
        };
      } else {
        delete variables.questionFilter;
      }

      return {
        ...state,
        questionId,
        variables
      };
    }
    case actionTypes.setStandardId: {
      const standardId = payload;
      const {variables} = state;
      if (standardId) {
        variables.standardFilter = {
          type: 'standards',
          field: 'id',
          operator: 'equals',
          values: [standardId]
        };
      } else {
        delete variables.standardFilter;
      }

      return {
        ...state,
        standardId,
        variables
      };
    }
    case actionTypes.setStandardSets: {
      const standardSetIds = payload;
      const {variables} = state;
      if (standardSetIds) {
        variables.standardSetFilter = {
          type: 'standard_sets',
          field: 'id',
          operator: 'equals',
          values: payload.split(',')
        };
      } else {
        delete variables.standardSetFilter;
      }

      return {
        ...state,
        standardSets: standardSetIds,
        variables: {...variables}
      };
    }
    case actionTypes.setSubject: {
      const subject = payload;
      const {variables} = state;
      if (subject) {
        variables.subjectFilter = {
          type: 'subjects',
          field: 'id',
          operator: 'equals',
          values: [subject]
        };
        variables.guideLevelFilter = undefined;
      } else {
        variables.subjectFilter = undefined;
      }
      return {
        ...state,
        subject,
        variables
      };
    }
    case actionTypes.setStudentId: {
      const studentId = payload;
      const {variables} = state;

      if (studentId) {
        variables.studentFilter = {
          type: 'students',
          field: 'id',
          operator: 'equals',
          values: [studentId]
        };
      } else {
        delete variables.studentFilter;
      }

      return {
        ...state,
        studentId,
        variables
      };
    }

    case actionTypes.setPage: {
      return {
        ...state,
        page: payload
      };
    }

    case actionTypes.setLimit: {
      return {
        ...state,
        limit: payload
      };
    }

    case actionTypes.setTotals: {
      return {
        ...state,
        totals: payload
      };
    }

    case actionTypes.setSort: {
      if (payload) {
        const sortsInQuery = (payload as string).split(',');
        const sorts = sortsInQuery.map((sort) => {
          const [sortBy, sortDirection] = sort.split(':');

          const [sortType, sortField] = sortBy.split('.');

          return {
            type: sortType,
            field: sortField,
            direction: sortDirection as 'asc' | 'desc'
          };
        });

        return {
          ...state,
          page: '1',
          sort: sorts
        };
      }

      return {
        ...state,
        sort: []
      };
    }

    default: {
      throw new Error(`Unhandled action type: ${type}`);
    }
  }
}

export default function useReportRoute({location, params}: Route): ContextType {
  const {
    topLevelFilterType = '' as TopLevelFilter,
    topLevelFilterId = '',
    assignmentId = '',
    questionId = '',
    standardId = '',
    studentId = ''
  } = params;
  const {pathname, query} = location;

  const metricsDefaults = pathname.endsWith('gradebook')
    ? 'performance'
    : 'time_spent,answer_breakdown,performance';

  const sortDefaults = pathname.endsWith('gradebook')
    ? 'students.last_name:asc,assignments.name:asc'
    : '';

  const gradeIsAvailable =
    (pathname.endsWith('assignments') && studentId) ||
    (pathname.endsWith('students') && assignmentId) ||
    (studentId && assignmentId) ||
    pathname.endsWith('gradebook');

  const {
    attempt = pathname.includes(DIMENSIONS.assignments) || pathname.includes('gradebook')
      ? '1'
      : 'mostRecent',
    limit = '25',
    metrics = metricsDefaults,
    page = '1',
    range = RANGES.THIS_SCHOOL_YEAR,
    s: keyword = '',
    sort = sortDefaults,
    start = '',
    subject = '',
    end = '',
    standardSets = '',
    personalization = ''
  } = query;

  const performanceCalculateBy = gradeIsAvailable
    ? query.performanceCalculateBy || 'grade'
    : 'accuracy';

  const initialMetrics = getMetricColumns(metrics.split(','));

  const initialState: State = {
    dimensions: [],
    attemptNumber: attempt === 'mostRecent' ? attempt : parseInt(attempt, 10),
    topLevelFilterType,
    assignmentId,
    topLevelFilterId,
    error: false,
    data: [],
    keyword,
    range,
    start,
    end,
    guideLevel: [],
    limit,
    totals: [],
    loading: true,
    loadingMore: false,
    performanceCalculateBy: 'accuracy',
    metrics: initialMetrics,
    page,
    path: [],
    questionId,
    sort: [],
    standardId,
    standardSets,
    autoPersonalization: !!personalization,
    studentId,
    subject,
    variables: {},
    cancelRequest: () => {}
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const actions = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(actionTypes).map(([action, type]) => [
          action,
          (payload) => {
            dispatch({type, payload});
          }
        ])
      ),
    [dispatch]
  ) as DispatchActions;

  useEffect(() => {
    (async () => {
      if (topLevelFilterId && !start && !end) {
        if (topLevelFilterType === 'classrooms') {
          const classroom = await getClassroomQuery(topLevelFilterId).getResourcePromise();
          if (classroom.getStatus() === CLASSROOM_STATUSES.ARCHIVED) {
            const {start: newStart, end: newEnd, range: newRange} = getArchivedDateRange(classroom);
            actions.setDate({
              start: newStart,
              end: newEnd,
              range: newRange
            });
          } else {
            const schoolYearDateRange = getThisSchoolYear();
            actions.setDate({
              start: schoolYearDateRange.get('start').format('YYYY-MM-DD'),
              end: schoolYearDateRange.get('end').format('YYYY-MM-DD'),
              range: RANGES.THIS_SCHOOL_YEAR
            });
          }
        } else {
          const schoolYearDateRange = getThisSchoolYear();
          actions.setDate({
            start: schoolYearDateRange.get('start').format('YYYY-MM-DD'),
            end: schoolYearDateRange.get('end').format('YYYY-MM-DD'),
            range: RANGES.THIS_SCHOOL_YEAR
          });
        }
      }
    })();
  }, [actions, topLevelFilterType, topLevelFilterId, end, start]);

  useEffect(() => {
    actions.setAssignmentId(assignmentId);
  }, [actions, assignmentId]);

  useEffect(() => {
    actions.setAttemptNumber(attempt);
  }, [actions, attempt]);

  useEffect(() => {
    actions.setTopLevelFilters({topLevelFilterType, topLevelFilterId});
  }, [actions, topLevelFilterType, topLevelFilterId]);

  useEffect(() => {
    actions.setStandardSets(standardSets);
  }, [actions, standardSets]);

  useEffect(() => {
    actions.setAutoPersonalization(!!personalization);
  }, [actions, personalization]);

  useEffect(() => {
    const dimensionsInPath = pathname
      .split('/')
      .filter((el) => isDimension(el) || el === 'gradebook');
    const currentDimension = dimensionsInPath[dimensionsInPath.length - 1];

    const currentDimensions =
      {
        assignments: ['assignments'],
        questions: ['questions'],
        students: ['students'],
        standards: ['standards'],
        gradebook: ['students', 'assignments']
      }[currentDimension] || [];

    actions.setDimensions(currentDimensions);
  }, [actions, pathname]);

  useEffect(() => {
    const datesAreValid = start && end && moment(start).isValid() && moment(end).isValid();
    if (datesAreValid) {
      actions.setDate({start, end, range});
    }
  }, [actions, start, end, range]);

  useEffect(() => {
    actions.setMetric(metrics);
  }, [actions, metrics]);

  useEffect(() => {
    const path = getPathFromPathname(pathname);
    actions.setPath(path);
  }, [actions, pathname]);

  useEffect(() => {
    const guideLevelIndexes = Object.keys(query).filter((item) => item.startsWith('guideLevel'));
    const formattedGuideLevels = guideLevelIndexes.map((index) => query[index]);

    if (!isEqual(formattedGuideLevels, state.variables.guideLevelFilter?.values)) {
      actions.setGuideLevelId(formattedGuideLevels);
    }
  }, [actions, query, state.variables.guideLevelFilter?.values]);

  useEffect(() => {
    actions.setQuestionId(questionId);
  }, [actions, questionId]);

  useEffect(() => {
    actions.setSubject(subject);
  }, [actions, subject]);

  useEffect(() => {
    actions.setStandardId(standardId);
  }, [actions, standardId]);

  useEffect(() => {
    actions.setStudentId(studentId);
  }, [actions, studentId]);

  useEffect(() => {
    actions.setKeyword(undefined);
  }, [
    actions,
    assignmentId,
    topLevelFilterType,
    topLevelFilterId,
    questionId,
    standardId,
    studentId
  ]);

  useEffect(() => {
    actions.setKeyword(keyword);
  }, [actions, keyword]);

  useEffect(() => {
    actions.setLimit(limit);
  }, [actions, limit]);

  useEffect(() => {
    actions.setPage(page);
  }, [actions, page]);

  useEffect(() => {
    actions.setSort(sort);
  }, [actions, sort]);

  useEffect(() => {
    actions.setPerformanceCalculateBy(performanceCalculateBy);
  }, [actions, performanceCalculateBy]);

  useEffect(() => {
    // we only allow gradebook for classroom ID filters, so don't bother fetching it otherwise
    const isNonClassroomIdGradebook =
      topLevelFilterType !== 'classrooms' && topLevelFilterId && pathname.endsWith('gradebook');

    if (topLevelFilterId && state.dimensions.length > 0 && !isNonClassroomIdGradebook) {
      decoratedFetchData(
        {
          data: [],
          fetchMore: false,
          metrics: state.metrics,
          variables: state.variables,
          dimensions: state.dimensions,
          limit,
          page,
          sort: state.sort,
          performanceCalculateBy: state.performanceCalculateBy,
          cancelRequest: state.cancelRequest,
          topLevelFilterType
        },
        actions.setLoading,
        actions.setData,
        actions.setTotals,
        actions.setError,
        actions.setCancelRequest,
        actions.setLoadingMore
      );
    } else {
      actions.setData([]);
      actions.setLoading(false);
      actions.setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actions,
    state.metrics,
    state.variables,
    state.variables.assignmentFilter,
    state.variables.attemptNumberFilter,
    state.variables.classroomFilter,
    state.variables.teacherFilter,
    state.variables.endDateFilter,
    state.variables.guideLevelFilter,
    state.variables.questionFilter,
    state.sort,
    state.variables.standardFilter,
    state.variables.startDateFilter,
    state.variables.studentFilter,
    state.variables.subjectFilter,
    state.dimensions,
    limit,
    page,
    topLevelFilterType,
    topLevelFilterId,
    state.performanceCalculateBy
  ]);

  return {
    ...state,
    ...actions,
    fetchData: () => {
      decoratedFetchData(
        {
          data: state.data,
          fetchMore: false,
          metrics: state.metrics,
          variables: state.variables,
          dimensions: state.dimensions,
          limit,
          page,
          sort: state.sort,
          performanceCalculateBy: state.performanceCalculateBy,
          cancelRequest: state.cancelRequest,
          topLevelFilterType
        },
        actions.setLoading,
        actions.setData,
        actions.setTotals,
        actions.setError,
        actions.setCancelRequest,
        actions.setLoadingMore
      );
    },
    fetchMoreData: () => {
      decoratedFetchData(
        {
          data: state.data,
          fetchMore: true,
          metrics: state.metrics,
          variables: state.variables,
          dimensions: state.dimensions,
          limit,
          page,
          sort: state.sort,
          performanceCalculateBy: state.performanceCalculateBy,
          cancelRequest: state.cancelRequest,
          topLevelFilterType
        },
        actions.setLoading,
        actions.setData,
        actions.setTotals,
        actions.setError,
        actions.setCancelRequest,
        actions.setLoadingMore
      );
    },
    query
  };
}
